import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { Router } from "@angular/router";
import { DeviceInventoryService } from "src/app/services/device-inventory/device-inventory.service";
import { DropdownsService } from "src/app/services/dropdowns/dropdowns.service";
import { Device } from "src/app/models/device.model";
import { Dropdown } from "src/app/models/dropdown.model";
import { PageEvent } from "@angular/material/paginator";

interface ExtendedDropdown extends Dropdown {
  displayDescription: string;
}
import { ToastrService } from "ngx-toastr";
import { MatDialog } from "@angular/material/dialog";
import { UploadDialogComponent } from "../upload-dialog/upload-dialog.component";
import { CustomerDetailsDialogComponent } from "../customer-details-dialog/customer-details-dialog.component";
import * as XLSX from 'xlsx';
  

@Component({
  selector: "app-device-inventory-list",
  templateUrl: "./device-inventory-list.component.html",
  styleUrls: ["./device-inventory-list.component.scss"],
})
export class DeviceInventoryListComponent implements OnInit {
  @ViewChild("fileInput", { static: false }) fileInput!: ElementRef;

  public devices: Device[] = [];
  public loading = false;
  public searchQuery = "";
  public selectedDeviceType = "";
  public openMenuIndex = -1;
  public deviceTypes: ExtendedDropdown[] = [];
  public sensorProfiles: ExtendedDropdown[] = [];
  public statuses: ExtendedDropdown[] = [];

  public totalElements = 0;
  public pageIndex = 0;
  public pageSize = 20;
  public pageSizeOptions = [10, 20, 50, 100];

  constructor(
    private _deviceInventoryService: DeviceInventoryService,
    private _dropdownService: DropdownsService,
    private _router: Router,
    private _toastr: ToastrService,
    private _dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadDropdownData();
    this.loadDevices();
  }

  loadDropdownData(): void {
    // Load device types
    this._dropdownService.getDeviceTypes().subscribe((res) => {
      let filteredTypes = res
        .filter((item) => item.code == 3 || item.code == 5)
        .map((item) => {
          let displayDescription = item.code == 3 ? "Gateway" : "ThingSendNode";
          return {
            ...item,
            displayDescription: displayDescription,
          };
        });
      this.deviceTypes = filteredTypes;
    });

    // Load sensor profiles
    this._dropdownService.getSensorProfiles().subscribe((res) => {
      let filteredProfiles = res
        .filter((item) => item.code == 2 || item.code == 4 || item.code == 5)
        .map((item) => {
          let displayDescription =
            item.code == 2
              ? "SensorTemp (Temp)"
              : item.code == 4
              ? "SensorTHM (Temp, Humidity)"
              : "SensorTHCO (Temp, Humidity, CO2)";
          return {
            ...item,
            displayDescription: displayDescription,
          };
        });
      this.sensorProfiles = filteredProfiles;
    });

    // Load statuses
    this._dropdownService.getStatuses().subscribe((res) => {
      this.statuses = res.map((item) => ({
        ...item,
        displayDescription: item.description
      }));
    });
  }

  loadDevices(): void {
    this.loading = true;
    let params = "";
    if (this.searchQuery) {
      params += `&device_id=${this.searchQuery}`;
    }
    if (this.selectedDeviceType) {
      params += `&device_type_id=${this.selectedDeviceType}`;
    }
    this._deviceInventoryService
      .getAll(params, this.pageIndex, this.pageSize)
      .subscribe({
        next: (response) => {
          this.devices = response.content;
          this.totalElements = response.totalElements;
          this.loading = false;
        },
        error: (error) => {
          this._toastr.error("Error loading devices", "", { timeOut: 3000 });
          this.loading = false;
        },
      });
  }

  onSearch(): void {
    this.pageIndex = 0; // Reset to first page when searching
    this.loadDevices();
  }

  onEnvironmentChange(): void {
    this.loadDevices();
  }

  onDeviceTypeChange(): void {
    this.pageIndex = 0; // Reset to first page when filtering
    this.loadDevices();
  }
  getEnvironmentName(env: string): string {
    if (env == null || env == undefined) {
      return "None";
    }
    switch (env.toLowerCase()) {
      case "qa":
        return "QA";
      case "prod":
        return "PROD";
      default:
        return 'None';
    }
  }
  getEnvColor(env: string): string {
    if (!env) return 'red';
    
    switch (env.toLowerCase()) {
      case 'none': return 'red';
      case 'qa': return 'orange';
      case 'prod': return 'green';
      default: return 'red';
    }
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadDevices();
  }

  onAddDevice(): void {
    this._router.navigate(["/landing/common-inventory/add"]);
  }

  toggleMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? -1 : index;
  }

  closeMenu(): void {
    this.openMenuIndex = -1;
  }

  onTableClick(event: Event): void {
    if (!(event.target as HTMLElement).closest('.action-wrapper')) {
      this.closeMenu();
    }
  }

  onUpdateDevice(deviceId: number): void {
    this._router.navigate([
      "/device-management/device-inventory/update",
      deviceId,
    ]);
  }

  onDeleteDevice(deviceId: number): void {
    if (confirm("Are you sure you want to delete this device?")) {
      this._deviceInventoryService.delete(deviceId).subscribe({
        next: () => {
          this._toastr.success("Device deleted successfully", "", {
            timeOut: 3000,
          });
          const remainingDevicesOnPage = this.devices.length - 1;
          if (remainingDevicesOnPage === 0 && this.pageIndex > 0) {
            this.pageIndex--;
          }
          this.loadDevices();
        },
        error: (error) => {
          this._toastr.error("Error deleting device", "", { timeOut: 3000 });
        },
      });
    }
  }

  public statusLabels: { [key: number]: string } = {
    1: "ACTIVE",
    2: "INACTIVE",
    3: "OFFLINE",
    4: "ONSITE",
    5: "FAULTY",
  };

  getStatusName(statusId: number): string {
    return this.statusLabels[statusId] || "UNKNOWN";
  }

  isColdStoreAssociated(device: Device): boolean {
    return device.coldStoreId !== null && device.coldStoreId !== undefined;
  }

  getDeviceTypeName(deviceTypeId: number): string {
    const deviceType = this.deviceTypes.find(
      (type) => type.code === deviceTypeId
    );
    return deviceType ? deviceType.displayDescription : deviceTypeId.toString();
  }

  getSensorProfileName(sensorProfileId: number): string {
    const sensorProfile = this.sensorProfiles.find(
      (profile) => profile.code === sensorProfileId
    );
    return sensorProfile
      ? sensorProfile.displayDescription
      : sensorProfileId.toString();
  }

  addColdStoreDetails(device: Device): void {
    this.closeMenu();
    const dialogRef = this._dialog.open(CustomerDetailsDialogComponent, {
      width: "600px",
      disableClose: true,
      data: { showEnvironmentSelection: true },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._deviceInventoryService
          .saveColdStore(
            {
              ...result,
              deviceId: device.deviceId,
              coordinates: result.latitude + "," + result.longitude,
            },
            result.environment
          )
          .subscribe({
            next: () => {
              this._toastr.success(
                "Cold Store details saved successfully.",
                "",
                { timeOut: 3000 }
              );
              this.loadDevices(); // Reload the list to show updated status
            },
            error: (error) => {
              this._toastr.error("Error saving cold store details.", "Error");
            },
          });
      }
    });
  }

  downloadTemplate(): void {
    this._deviceInventoryService.downloadTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "device_template.xlsx";
        link.click();
        window.URL.revokeObjectURL(url);
      }
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx')) {
        this._toastr.error('Please upload an Excel file (.xlsx)', '', { timeOut: 3000 });
        this.fileInput.nativeElement.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const devices = this.parseExcelFileData(data);

          const dialogRef = this._dialog.open(UploadDialogComponent, {
            width: '1200px',
            maxWidth: '95vw',
            data: { devices },
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              this._deviceInventoryService.bulkUpload(result).subscribe({
                next: () => {
                  this._toastr.success('Devices uploaded successfully', '', {
                    timeOut: 3000,
                  });
                  this.loadDevices();
                },
                error: (error) => {
                  this._toastr.error('Error uploading devices', '', {
                    timeOut: 3000,
                  });
                },
              });
            }
            this.fileInput.nativeElement.value = '';
          });
        } catch (error) {
          this._toastr.error('Error processing Excel file', '', {
            timeOut: 3000,
          });
          this.fileInput.nativeElement.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

 
  private parseExcelFileData(data: Uint8Array): Partial<Device>[] {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const json: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    if (!json || json.length < 2) return [];

    const headers: string[] = json[0].map((h: any) => (h || '').toString().trim());
    const devices: Partial<Device>[] = [];

    for (let i = 1; i < json.length; i++) {
      const row = json[i];
      if (!row || row.length === 0 || row.every((cell: any) => !cell || cell.toString().trim() === '')) continue;
      const device: Partial<Device> = {};
      headers.forEach((header, idx) => {
        const value = row[idx];
        switch (header) {
          case 'Device Type':
            if (value) {
              const typeValue = value.toString().trim();
              const deviceType = this.deviceTypes.find(dt => 
                dt.description.toLowerCase() === typeValue.toLowerCase() ||
                (dt.displayDescription && dt.displayDescription.toLowerCase() === typeValue.toLowerCase())
              );
              device.deviceType = deviceType ? deviceType.code : typeValue;
            }
            break;
          case 'Sensor Profile':
            if (value) {
              const profileValue = value.toString().trim();
              const sensorProfile = this.sensorProfiles.find(sp => 
                sp.description.toLowerCase() === profileValue.toLowerCase() ||
                (sp.displayDescription && sp.displayDescription.toLowerCase() === profileValue.toLowerCase())
              );
              device.sensorProfile = sensorProfile ? sensorProfile.code : profileValue;
            }
            break;
          case 'Device Id':
            device.deviceId = value ? value.toString().trim() : undefined;
            break;
          case 'Start of Service':
            device.startOfService = value ? this.parseDate(value) : undefined;
            break;
          case 'End of Life Cycle':
            device.endOfLifecycle = value ? this.parseDate(value) : undefined;
            break;
          case 'End of License':
            device.endOfLicense = value ? this.parseDate(value) : undefined;
            break;
          case 'FW Revision':
            device.fwRevision = value ? value.toString().trim() : undefined;
            break;
          case 'HW Revision':
            device.hwRevision = value ? value.toString().trim() : undefined;
            break;
          case 'Status':
            if (value) {
              const statusValue = value.toString().trim();
              const statusObj = this.statuses.find(s => 
                s.description.toLowerCase() === statusValue.toLowerCase()
              );
              if (statusObj) {
                device.statusId = statusObj.code;
              } else {
                const numericValue = parseInt(statusValue);
                device.statusId = !isNaN(numericValue) ? numericValue : statusValue;
              }
            }else {
              device.statusId = 1;
            }
            break;
        }
      });
      if (device.deviceId && device.deviceType && device.sensorProfile) {
        devices.push(device);
      }
    }
    return devices;
  }

  private parseDate(value: any): Date | undefined {
    if (!value) return undefined;
    
    try {
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? undefined : value;
      }
      
      if (typeof value === 'number') {
        const excelEpoch = new Date(1899, 11, 30);
        const jsDate = new Date(excelEpoch.getTime() + (value * 24 * 60 * 60 * 1000));
        return isNaN(jsDate.getTime()) ? undefined : jsDate;
      }
      
      if (typeof value === 'string') {
        const str = value.toString().trim();
        
        if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
          const date = new Date(str);
          return isNaN(date.getTime()) ? undefined : date;
        }
        
        const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        const match = str.match(datePattern);
        if (match) {
          const [, part1, part2, year] = match;
          const month1 = parseInt(part1, 10);
          const day1 = parseInt(part2, 10);
          
         
          let month, day;
          if (month1 > 12) {
            month = day1;
            day = month1;
          } else if (day1 > 12) {
            month = month1;
            day = day1;
          } else {
            month = month1;
            day = day1;
          }
          
          const date = new Date(parseInt(year, 10), month - 1, day);
          return isNaN(date.getTime()) ? undefined : date;
        }
        
        const date = new Date(str);
        return isNaN(date.getTime()) ? undefined : date;
      }
      
      const date = new Date(value);
      return isNaN(date.getTime()) ? undefined : date;
    } catch (error) {
      console.warn('Error parsing date:', value, error);
      return undefined;
    }
  }

  public deassociate(device:Device){
    this.closeMenu();
    if(confirm("Are you sure you want to deassociate cold store from this device?")){
      this._deviceInventoryService.deassociateColdStore(device.deviceId, device.associated_env).subscribe({
        next: () => {
          this._toastr.success("Cold Store deassociated successfully", "", { timeOut: 3000 });
          this.loadDevices();
        },
        error: (error) => {
          this._toastr.error("Error deassociating Cold Store", "", { timeOut: 3000 });
        }
      });
    }
  }

  public moveToQA(device: Device) {
    this.closeMenu();
     const dialogRef = this._dialog.open(CustomerDetailsDialogComponent, {
      width: "600px",
      disableClose: true,
      data: { showEnvironmentSelection: false, environment: 'qa' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._deviceInventoryService
          .saveColdStore(
            {
              ...result,
              deviceId: device.deviceId,
              coordinates: result.latitude + "," + result.longitude,
            },
            result.environment
          )
          .subscribe({
            next: () => {
              this._toastr.success(
                "Cold Store details saved successfully.",
                "",
                { timeOut: 3000 }
              );
              this.loadDevices(); // Reload the list to show updated status
            },
            error: (error) => {
              this._toastr.error("Error saving cold store details.", "Error");
            },
          });
      }
    });
  }

  public deployDevice(device: Device) {
    this.closeMenu();
    const dialogRef = this._dialog.open(CustomerDetailsDialogComponent, {
      width: "600px",
      disableClose: true,
      data: { showEnvironmentSelection: false, environment: 'prod', coldStoreData: device.coldStore },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._deviceInventoryService
          .deployDevice(
            {
              ...result,
              deviceId: device.deviceId,
              coordinates: result.latitude + "," + result.longitude,
              oldColdStoreId: device.coldStoreId
            },
            result.environment
          )
          .subscribe({
            next: () => {
              this._toastr.success(
                "Cold Store details saved successfully.",
                "",
                { timeOut: 3000 }
              );
              this.loadDevices(); 
            },
            error: (error) => {
              this._toastr.error("Error saving cold store details.", "Error");
            },
          });
      }
    });
  }
}
